import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import type { GetTicketsRequestParamsDTO, GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { TicketDetailsI } from 'declarations/interfaces/tickets';
import type { NoopT } from 'declarations/types/common';
import { useFetchTicket } from 'hooks/api/useTickets';

import { useMessages } from '../components/Messages/contexts/MessagesContext';

type UseActionsMenuT = {
  ticketDetails: TicketDetailsI | null;
  isTicketDetailsFetchingError: boolean;
  isLoadingTicketDetails: boolean;
  isTicketReopenPopupOpened: boolean;
  isTicketApprovePopupOpened: boolean;
  isTicketClosePopupOpened: boolean;
  isAddAttachmentPopupOpened: boolean;
  isViewAttachmentsPopupOpened: boolean;
  isChangeClaimStatusPopupOpened: boolean;
  isChangePriorityPopupOpened: boolean;
  isDeleteTicketPopupOpened: boolean;
  selectedTicketId: string | null;
  setTicketId: (id: string | null) => void;
  fetchTicketsParams: GetTicketsRequestParamsDTO;
  handleTabChange: (ticketId: string) => void;
  handleOpenViewAttachmentsPopup: NoopT;
  handleCloseViewAttachmentsPopup: NoopT;
  handleOpenAddAttachmentPopup: NoopT;
  handleCloseAddAttachmentPopup: NoopT;
  handleOpenTicketReopenPopup: NoopT;
  handleCloseTicketReopenPopup: NoopT;
  handleCloseTicketDetailsPopup: NoopT;
  handleOpenTicketApprovePopup: NoopT;
  handleCloseTicketApprovePopup: NoopT;
  handleOpenTicketClosePopup: NoopT;
  handleCloseTicketClosePopup: NoopT;
  handleOpenClaimStatusChangePopup: NoopT;
  handleCloseClaimStatusChangePopup: NoopT;
  handleOpenPriorityChangePopup: NoopT;
  handleClosePriorityChangePopup: NoopT;
  handleOpenDeleteTicketPopup: NoopT;
  handleCloseDeleteTicketPopup: NoopT;
  handleOpenReassignTicketPopup: NoopT;
  handleCloseReassignTicketPopup: NoopT;
  handleOpenShareTicketPopup: NoopT;
  handleCloseShareTicketPopup: NoopT;
};

const useTicketReopenApprovalPopup = (): UseActionsMenuT => {
  const [isTicketReopenPopupOpened, setTicketReopenPopupVisibility] = useState(false);
  const [isTicketApprovePopupOpened, setTicketApprovePopupVisibility] = useState(false);
  const [isTicketClosePopupOpened, setTicketClosePopupVisibility] = useState(false);
  const [isAddAttachmentPopupOpened, setAddAttachmentPopupVisibility] = useState(false);
  const [isViewAttachmentsPopupOpened, setViewAttachmentsPopupVisibility] = useState(false);
  const [isChangeClaimStatusPopupOpened, setChangeClaimStatusPopupVisibility] = useState(false);
  const [isChangePriorityPopupOpened, setChangePriorityPopupVisibility] = useState(false);
  const [isDeleteTicketPopupOpened, setDeleteTicketPopupVisibility] = useState(false);
  const [, setShareTicketPopupVisibility] = useState(false);
  const [, setReassignTicketPopupVisibility] = useState(false);
  const { setTicketId, setSelectedTicketsIds, selectedTicketId, fetchTicketsParams } = useTickets();
  const { debouncedCreateTicketMessage, unsavedMessage } = useMessages();
  const queryClient = useQueryClient();

  const ticketDetailsResult = useFetchTicket({
    ticketId: selectedTicketId || '',
    onSuccess: (updatedTicket): void => {
      queryClient.setQueryData<InfiniteData<GetTicketsResponseDTO>>(
        [RequestQueryKeysE.FetchTickets, fetchTicketsParams],
        previousData => {
          return previousData
            ? {
                pageParams: previousData.pageParams,
                pages: previousData.pages.map(page => ({
                  data: page.data.map(ticket => {
                    if (ticket.id === updatedTicket.id && !ticket.isViewed) {
                      return {
                        ...ticket,
                        isViewed: true,
                      };
                    }

                    return ticket;
                  }),
                  count: page.count,
                })),
              }
            : undefined;
        }
      );
    },
  });

  const handleCloseTicketDetailsPopup = useCallback((): void => {
    setTicketId(null);
    setSelectedTicketsIds([]);

    if (unsavedMessage) {
      debouncedCreateTicketMessage.flush();
    }
  }, [setTicketId, setSelectedTicketsIds, unsavedMessage, debouncedCreateTicketMessage]);
  const handleOpenTicketReopenPopup = useCallback((): void => {
    setTicketReopenPopupVisibility(true);
  }, []);
  const handleCloseTicketReopenPopup = useCallback((): void => {
    setTicketReopenPopupVisibility(false);
  }, []);
  const handleOpenTicketApprovePopup = useCallback((): void => {
    setTicketApprovePopupVisibility(true);
  }, []);
  const handleCloseTicketApprovePopup = useCallback((): void => {
    setTicketApprovePopupVisibility(false);
  }, []);
  const handleOpenTicketClosePopup = useCallback((): void => {
    setTicketClosePopupVisibility(true);
  }, []);
  const handleCloseTicketClosePopup = useCallback((): void => {
    setTicketClosePopupVisibility(false);
    queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
  }, [queryClient]);
  const handleOpenAddAttachmentPopup = useCallback((): void => {
    setAddAttachmentPopupVisibility(true);
  }, []);
  const handleCloseAddAttachmentPopup = useCallback((): void => {
    setAddAttachmentPopupVisibility(false);
  }, []);
  const handleOpenViewAttachmentsPopup = useCallback((): void => {
    setViewAttachmentsPopupVisibility(true);
  }, []);
  const handleCloseViewAttachmentsPopup = useCallback((): void => {
    setViewAttachmentsPopupVisibility(false);
  }, []);
  const handleOpenClaimStatusChangePopup = useCallback((): void => {
    setChangeClaimStatusPopupVisibility(true);
  }, []);
  const handleCloseClaimStatusChangePopup = useCallback((): void => {
    setChangeClaimStatusPopupVisibility(false);
  }, []);
  const handleOpenPriorityChangePopup = useCallback((): void => {
    setChangePriorityPopupVisibility(true);
  }, []);
  const handleClosePriorityChangePopup = useCallback((): void => {
    setChangePriorityPopupVisibility(false);
  }, []);
  const handleOpenDeleteTicketPopup = useCallback((): void => {
    setDeleteTicketPopupVisibility(true);
  }, []);
  const handleCloseDeleteTicketPopup = useCallback((): void => {
    setDeleteTicketPopupVisibility(false);
  }, []);
  const handleOpenShareTicketPopup = useCallback((): void => {
    setShareTicketPopupVisibility(true);
  }, []);
  const handleCloseShareTicketPopup = useCallback((): void => {
    setShareTicketPopupVisibility(false);
  }, []);
  const handleOpenReassignTicketPopup = useCallback((): void => {
    setReassignTicketPopupVisibility(true);
  }, []);
  const handleCloseReassignTicketPopup = useCallback((): void => {
    setReassignTicketPopupVisibility(false);
  }, []);

  const handleTabChange = useCallback(
    (ticketId: string): void => {
      setTicketId(ticketId);
    },
    [setTicketId]
  );

  return {
    ticketDetails: ticketDetailsResult.data ?? null,
    isTicketDetailsFetchingError: ticketDetailsResult.isError,
    isLoadingTicketDetails: ticketDetailsResult.isLoading || ticketDetailsResult.isRefetching,
    selectedTicketId,
    setTicketId,
    fetchTicketsParams,
    handleTabChange,
    isTicketReopenPopupOpened,
    handleCloseTicketDetailsPopup,
    handleOpenTicketReopenPopup,
    handleCloseTicketReopenPopup,
    isTicketApprovePopupOpened,
    handleOpenTicketApprovePopup,
    handleCloseTicketApprovePopup,
    isTicketClosePopupOpened,
    handleOpenTicketClosePopup,
    handleCloseTicketClosePopup,
    isAddAttachmentPopupOpened,
    handleOpenAddAttachmentPopup,
    handleCloseAddAttachmentPopup,
    isViewAttachmentsPopupOpened,
    handleOpenViewAttachmentsPopup,
    handleCloseViewAttachmentsPopup,
    isChangeClaimStatusPopupOpened,
    handleOpenClaimStatusChangePopup,
    handleCloseClaimStatusChangePopup,
    isChangePriorityPopupOpened,
    handleOpenPriorityChangePopup,
    handleClosePriorityChangePopup,
    isDeleteTicketPopupOpened,
    handleOpenDeleteTicketPopup,
    handleCloseDeleteTicketPopup,
    handleOpenShareTicketPopup,
    handleCloseShareTicketPopup,
    handleOpenReassignTicketPopup,
    handleCloseReassignTicketPopup,
  };
};

export default useTicketReopenApprovalPopup;
